import { supabase } from '@/lib/supabase';
import type { ManualFeatureType } from '@/config/manualCadFeatures';

// Client for the `manual-cad` edge fn → build123d `/manual/*` session API.
// Mirrors the whitelisted feature schema the microservice validates
// (microservice/manual_cad.py). See docs/freecad-manual-cad-integration-plan.md.

export type ManualPlane = 'XY' | 'XZ' | 'YZ';

export type ManualProfile =
  | { kind: 'rectangle'; width: number; height: number }
  | { kind: 'circle'; radius: number };

export type ManualFeatureInput =
  | { type: 'Sketch'; plane: ManualPlane; profile: ManualProfile }
  | { type: 'Pad'; sketchId: string; length: number }
  | { type: 'Pocket'; sketchId: string; depth: number }
  | { type: 'Fillet'; radius: number }
  | { type: 'Chamfer'; distance: number }
  | { type: 'Boolean'; op: 'cut' | 'union' | 'intersect'; toolId: string };

/** A feature as stored server-side: the input shape + a server-assigned id. */
export interface ManualStoredFeature {
  id: string;
  type: ManualFeatureType;
  [k: string]: unknown;
}

/** Tessellated Tip geometry (base64), or an `error` when recompute failed. */
export interface ManualMesh {
  stl_b64?: string | null;
  step_b64?: string | null;
  edges_b64?: string | null;
  tangent_edges_b64?: string | null;
  error?: string | null;
}

/** The feature tree + (for mutations) the freshly recomputed Tip mesh. */
export interface ManualTree {
  sessionId: string;
  features: ManualStoredFeature[];
  tipId: string | null;
  mesh?: ManualMesh | null;
}

type Action =
  | 'create'
  | 'get'
  | 'addFeature'
  | 'editFeature'
  | 'deleteFeature'
  | 'export';

interface ManualRequest {
  action: Action;
  sessionId?: string;
  featureId?: string;
  feature?: ManualFeatureInput;
}

async function invoke<T>(body: ManualRequest): Promise<T> {
  const { data, error } = await supabase.functions.invoke('manual-cad', {
    body,
  });
  if (error) throw error;
  return data as T;
}

export const createManualSession = () =>
  invoke<ManualTree>({ action: 'create' });

export const getManualSession = (sessionId: string) =>
  invoke<ManualTree>({ action: 'get', sessionId });

export const addManualFeature = (
  sessionId: string,
  feature: ManualFeatureInput,
) => invoke<ManualTree>({ action: 'addFeature', sessionId, feature });

export const editManualFeature = (
  sessionId: string,
  featureId: string,
  feature: ManualFeatureInput,
) =>
  invoke<ManualTree>({ action: 'editFeature', sessionId, featureId, feature });

export const deleteManualFeature = (sessionId: string, featureId: string) =>
  invoke<ManualTree>({ action: 'deleteFeature', sessionId, featureId });

/** Export returns the Tip mesh directly (STEP + STL), not a tree. */
export const exportManualSession = (sessionId: string) =>
  invoke<ManualMesh>({ action: 'export', sessionId });
